import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import { useDispatch, useSelector } from 'react-redux';
import { getProductOptions } from 'src/redux/Actions/Settings';
import { ReactComponent as EmptyCheckIcon } from 'src/Images/icons/common/emptyCheckIcon.svg';
import { ReactComponent as AddIcon } from 'src/Images/icons/common/addIcon.svg';
import { ReactComponent as Delete } from 'src/Images/icons/common/delete.svg';
import { ReactComponent as Product } from 'src/Images/icons/common/product.svg';

export default function AddProductComponent({ formData, setFormData }) {
  const dispatch = useDispatch();
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const authReducer = useSelector((state) => state.AuthReducer);
  const themeColor = authReducer?.userData?.tenant?.theme;
  const tenantOrganisationId = authReducer?.userData?.organisations?.id;
  const productOptions = useSelector(
    (state) => state.SettingsReducer.productOptions,
  );
  const productList = productOptions?.products || [];
  const pagination = productOptions?.pagination || {};
  const [pageNumber, setPageNumber] = useState(1);
  const [allProductsCache, setAllProductsCache] = useState([]);

  useEffect(() => {
    const delay = setTimeout(() => {
      dispatch(
        getProductOptions(tenantOrganisationId, productSearchTerm, pageNumber),
      );
    }, 350);
    return () => clearTimeout(delay);
  }, [dispatch, tenantOrganisationId, productSearchTerm, pageNumber]);

  const handleSearchChange = (value) => {
    setProductSearchTerm(value);
    setPageNumber(1);
  };

  const handleAddProduct = (product) => {
    setFormData((prev) => {
      if (prev.productIds.some((productIds) => productIds.id === product.id))
        return prev;
      return {
        ...prev,
        productIds: [
          ...prev.productIds,
          { id: product.id, name: product.name },
        ],
      };
    });
  };

  const handleRemoveProduct = (id) => {
    setFormData((prev) => ({
      ...prev,
      productIds: prev.productIds.filter((productIds) => productIds.id !== id),
    }));
  };

  useEffect(() => {
    setAllProductsCache((previousProducts) => {
      const newProducts = productList.filter(
        (product) => !previousProducts.some((item) => item.id === product.id),
      );
      return [...previousProducts, ...newProducts];
    });
  }, [productList]);

  const changePage = (nextPage) => {
    const totalPages = pagination.total_pages || 1;
    if (nextPage >= 1 && nextPage <= totalPages) setPageNumber(nextPage);
  };

  const selectedProducts = (formData.productIds || [])
    .map(
      (p) => allProductsCache.find((productObj) => productObj.id === p.id) || p,
    )
    .filter(Boolean);

  return (
    <div className="div-flex-column rg-20 horizontal-padding-10">
      <Row className="g-3 padding-top-10">
        {/* Left: Product Selection */}
        <Col xxl={4} xl={4} lg={4}>
          <Row className="padding-bottom-10 padding-top-10 right-padding-10 ">
            <Col xxl={12} xl={12} lg={12}>
              <div className="input-wrapper">
                <input
                  className="form-input-section"
                  name="product"
                  placeholder="Search Product"
                  value={productSearchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
                <span className="form-input-section-icon">
                  <Search className="icon-size-sm" color="#9F9F9F" />
                </span>
              </div>
            </Col>
          </Row>
          <div
            className={`quality-check-product-left-height border-right padding-top-10 div-flex-column rg-10 theme-scrollable-container-${themeColor}`}
          >
            {productList.map((product) => {
              const isSelected = (formData.productIds || []).some(
                (productObj) => productObj.id === product.id,
              );
              return (
                <div
                  className="quality-check-list-data div-flex-row div-space-between border border-radius-4 right-margin-10"
                  key={product.id}
                >
                  <div className=" div-flex-column ">
                    <span className="text-md text-semi-bold">
                      {product.name}
                    </span>
                    <span className="text-xs text-grey">
                      {product.description}
                    </span>
                  </div>
                  <button
                    type="button"
                    aria-label={isSelected ? 'Remove product' : 'Add product'}
                    className="text-sm cursor-pointer bg-transparent border-0 p-0"
                    onClick={() => {
                      if (!isSelected) {
                        handleAddProduct(product);
                      }
                    }}
                  >
                    {isSelected ? (
                      <EmptyCheckIcon className="icon-size-lg" />
                    ) : (
                      <AddIcon className="icon-size-lg" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
          <div className="div-flex-row div-space-between div-align-center right-padding-10 padding-top-10">
            <button
              type="button"
              className="text-sm cursor-pointer bg-transparent border-0"
              onClick={() => changePage(pageNumber - 1)}
              disabled={pageNumber <= 1}
            >
              Previous
            </button>
            <span className="text-sm text-grey">
              Page {pagination.current_page || pageNumber} of{' '}
              {pagination.total_pages || 1}
            </span>
            <button
              type="button"
              className="text-sm cursor-pointer bg-transparent border-0"
              onClick={() => changePage(pageNumber + 1)}
              disabled={!pagination.has_next}
            >
              Next
            </button>
          </div>
        </Col>
        <Col xxl={8} xl={8} lg={8}>
          {selectedProducts.length === 0 ? (
            <div className="div-flex-column rg-10">
              <span className="text-md text-semi-bold padding-bottom-13 border-bottom padding-top-10">
                Selected Product
              </span>
              <div className="quality-check-empty-state-height div-flex-column-center">
                <Product className="icon-size-large" />
                <span className="text-xxl text-semi-bold">List is empty</span>
                <span className="text-sm text-grey">
                  Your selected product list is currently empty. <br />
                  Select the product form the left to add in the list for <br />
                  applying your selected dimensions.
                </span>
              </div>
            </div>
          ) : (
            <div className="div-flex-column rg-10">
              <span className="text-md text-semi-bold padding-bottom-13 border-bottom padding-top-10">
                Selected Product
              </span>
              <div className="quality-check-create-order-item-table-wrapper">
                <table className="quality-check-create-order-item-table">
                  <thead>
                    <tr>
                      <th className="table-head-40">
                        <span className="text-sm text-grey text-semi-bold">
                          #
                        </span>
                      </th>
                      <th className="table-head-100">
                        <span className="text-sm text-grey text-semi-bold">
                          Product Name
                        </span>
                      </th>
                      <th className="table-head-200">
                        <span className="text-sm text-grey text-semi-bold">
                          Description
                        </span>
                      </th>
                      <th className="table-head-40" aria-label="actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {selectedProducts.map((product, index) => (
                      <tr key={product.id}>
                        <td>
                          <span className="text-sm text-nowrap">
                            {index + 1}
                          </span>
                        </td>

                        <td>
                          <span className="text-sm text-nowrap">
                            {product.name}
                          </span>
                        </td>

                        <td>
                          <span className="text-sm text-nowrap">
                            {product.description ? product.description : '--'}
                          </span>
                        </td>
                        <td>
                          <Delete
                            className="icon-size-lg cursor-pointer div-flex-column div-align-center"
                            onClick={() => handleRemoveProduct(product.id)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}
